import { useEffect, useState } from "react";
import { api, inr, stageLabel, TASK_STATUSES } from "../lib/api";
import { Card } from "../components/ui/card";
import { PriorityBadge, StageBadge } from "../components/Badges";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertTriangle, Clock, Folders, ShieldCheck, Wrench, PackageSearch, TrendingUp, Wallet, ArrowDownRight, Users, Flame } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

function Stat({ label, value, icon: Icon, sub, tone = "primary", testid }) {  const tones = {
    primary: "text-white border-[hsl(var(--brand-blue))/0.5] bg-[hsl(var(--brand-blue))/0.15]",
    warning: "text-amber-400 border-amber-500/30 bg-amber-500/10",
    critical: "text-red-400 border-red-500/30 bg-red-500/10",
    success: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    brand: "text-[hsl(218_65%_10%)] border-[hsl(var(--brand-yellow))/0.6] bg-[hsl(var(--brand-yellow))]",
  };
  return (
    <Card className="p-4 bg-card border-border card-hover" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</div>
          <div className={`mt-1 font-display text-2xl font-bold tracking-tight ${tone === "brand" ? "text-[hsl(var(--brand-yellow))]" : ""}`}>{value}</div>
          {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
        </div>
        <div className={`h-9 w-9 rounded border grid place-items-center ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [hr, setHr] = useState(null);
  const [health, setHealth] = useState(null);

  const reload = () => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
    api.get("/dashboard/health-summary").then((r) => setHealth(r.data)).catch(() => setHealth(null));
  };
  useEffect(() => {
    reload();
    api.get("/hr/summary").then((r) => setHr(r.data)).catch(() => setHr({ open_openings: 0, upcoming_reviews: 0 }));
  }, []);
  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const stageEntries = Object.entries(data.projects_by_stage || {});

  return (
    <div className="space-y-2.5" data-testid="dashboard-page">

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2.5">
        <Stat label="Active Projects" value={data.active_projects_count} icon={Folders} sub="Across all stages" testid="stat-active-projects" />
        <Stat label="Hot Deals Value" value={inr(data.hot_deals_value ?? 0)} icon={Flame} tone="brand" sub={`${data.hot_deals_count ?? 0} high-priority`} testid="stat-hot-deals" />
        <Stat label="Pending Approvals" value={data.pending_approvals.length} icon={ShieldCheck} tone="warning" sub="Awaiting decision" testid="stat-pending-approvals" />
        <Stat label="Pipeline Value" value={inr(data.total_pipeline)} icon={TrendingUp} tone="success" sub="Total active" testid="stat-pipeline" />
      </div>

      {health && (
        <Card className="p-4 bg-card border-border" data-testid="dashboard-health">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h3 className="font-display font-semibold">Project Health</h3>
            <div className="text-xs text-muted-foreground">Auto-calculated from tasks, payments, procurement, drawings, approvals</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <HealthTile icon={Folders} label="On Track" value={health.buckets.on_track || 0} tone="success" testid="health-on-track" />
            <HealthTile icon={AlertTriangle} label="At Risk" value={health.buckets.at_risk || 0} tone="warning" testid="health-at-risk" />
            <HealthTile icon={Clock} label="Delayed" value={health.buckets.delayed || 0} tone="critical" testid="health-delayed" />
            <HealthTile icon={ArrowDownRight} label="On Hold" value={health.buckets.on_hold || 0} tone="primary" testid="health-on-hold" />
          </div>
          {health.attention?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Needs attention</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {health.attention.map((row) => (
                  <Link to={`/projects/${row.id}`} key={row.id}
                        className="flex items-center justify-between p-2 rounded border border-border bg-secondary/30 hover:border-primary/40 transition-colors">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{row.client_name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{stageLabel(row.stage)} · {row.reasons.join(" · ")}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs font-mono ${row.health === "delayed" ? "text-red-400" : "text-amber-400"}`}>{row.score}</span>
                      <span className="text-[10px] uppercase tracking-wider">{row.health.replace("_", " ")}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
        <Card className="p-4 bg-card border-border" data-testid="today-tasks-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold">Today&apos;s Priority</h3>
            <Link to="/tasks" className="text-xs text-primary hover:underline">View all →</Link>
          </div>
          {data.today_tasks.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded">No tasks due today. Enjoy the calm.</div>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {data.today_tasks.map((t) => (
                <TaskRow key={t.id} task={t} onChanged={reload} />
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4 bg-card border-border" data-testid="overdue-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-red-400">Overdue Tasks</h3>
            <span className="text-xs text-muted-foreground">{data.overdue_tasks.length} items</span>
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {data.overdue_tasks.slice(0, 8).map((t) => (
              <TaskRow key={t.id} task={t} onChanged={reload} overdue />
            ))}
            {data.overdue_tasks.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-4">Nothing overdue 🎉</div>
            )}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
        <Card className="p-4 bg-card border-border flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold">Project Stage Pipeline</h3>
            <Link to="/projects" className="text-xs text-primary hover:underline">Open pipeline →</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 flex-1 auto-rows-fr">
            {stageEntries.map(([k, v]) => (
              <div key={k} className="p-3 rounded border border-border bg-secondary/30 flex flex-col justify-between">
                <div className="text-[11px] text-muted-foreground leading-tight">{stageLabel(k)}</div>
                <div className="font-display text-2xl font-bold leading-none mt-2 self-start">{v}</div>
              </div>
            ))}
            {stageEntries.length === 0 && <div className="text-sm text-muted-foreground col-span-full">No active projects.</div>}
          </div>
        </Card>

        <Card className="p-4 bg-card border-border flex flex-col">
          <h3 className="font-display font-semibold mb-3">Functional Work</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 flex-1 auto-rows-fr" data-testid="function-health-grid">
            <HealthTile icon={PackageSearch} label="Procurement List" value={data.pending_procurement.length} tone="warning" testid="fh-procurement" />
            <HealthTile icon={Wrench} label="Open Service Tickets" value={data.open_tickets.length} tone="primary" testid="fh-tickets" />
            <HealthTile icon={Wallet} label="Payment Receivables" value={inrCompact(data.payment_receivables ?? data.payment_pending)} tone="success" testid="fh-receivables" />
            <HealthTile icon={ArrowDownRight} label="Payments Due" value={inrCompact(data.payments_due ?? 0)} tone="critical" testid="fh-payments-due" />
            <HealthTile icon={ShieldCheck} label="Approval Waiting" value={data.pending_approvals.length} tone="warning" testid="fh-approvals" />
            <HealthTile icon={Users} label="Human Resources" value={((hr?.open_openings ?? 0) + (hr?.upcoming_reviews ?? 0))} sub={hr ? `${hr.open_openings} open · ${hr.upcoming_reviews} reviews` : ""} tone="brand" testid="fh-hr" />
          </div>
        </Card>
      </div>
    </div>
  );
}

function HealthRow({ icon: Icon, label, value, tone }) {
  const tones = {
    primary: "text-primary bg-primary/10 border-primary/30",
    warning: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    critical: "text-red-400 bg-red-500/10 border-red-500/30",
  };
  return (
    <div className="flex items-center gap-3 p-2 rounded border border-border bg-secondary/20">
      <div className={`h-8 w-8 rounded grid place-items-center border ${tones[tone]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 text-sm">{label}</div>
      <div className="font-display font-semibold">{value}</div>
    </div>
  );
}

function HealthTile({ icon: Icon, label, value, sub, tone, testid }) {
  const tones = {
    primary: "text-white bg-[hsl(var(--brand-blue))/0.12] border-[hsl(var(--brand-blue))/0.5]",
    warning: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    critical: "text-red-300 bg-red-500/10 border-red-500/30",
    success: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    brand: "text-[hsl(var(--brand-yellow))] bg-[hsl(var(--brand-yellow))/0.12] border-[hsl(var(--brand-yellow))/0.5]",
  };
  const iconTones = {
    primary: "text-[hsl(var(--brand-blue))]",
    warning: "text-amber-400",
    critical: "text-red-400",
    success: "text-emerald-400",
    brand: "text-[hsl(var(--brand-yellow))]",
  };
  return (
    <div className={`p-3 rounded border ${tones[tone]} flex flex-col justify-between transition-transform hover:-translate-y-0.5`} data-testid={testid}>
      <Icon className={`h-4 w-4 ${iconTones[tone]}`} />
      <div>
        <div className="font-display font-bold text-2xl leading-none mt-1">{value}</div>
        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mt-1.5 leading-tight">{label}</div>
        {sub && <div className="text-[10px] text-muted-foreground/80 mt-0.5 font-mono">{sub}</div>}
      </div>
    </div>
  );
}

function inrCompact(n) {
  if (n == null) return "—";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
}

function TaskRow({ task, onChanged, overdue }) {
  const changeStatus = async (v) => {
    try {
      await api.patch(`/tasks/${task.id}`, { status: v });
      toast.success("Updated");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  return (
    <div
      className={`flex items-center gap-2 p-2 rounded border ${
        overdue ? "border-red-500/20 bg-red-500/5" : "border-border bg-secondary/30"
      }`}
      data-testid={`dashboard-task-${task.id}`}
    >
      <Clock className={`h-4 w-4 shrink-0 ${overdue ? "text-red-400" : "text-primary"}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{task.title}</div>
        <div className="text-xs text-muted-foreground">
          {task.function} · {overdue ? `Was due ${task.due_date}` : "Due today"}
        </div>
      </div>
      <PriorityBadge value={task.priority} />
      <Select value={task.status} onValueChange={changeStatus}>
        <SelectTrigger className="h-7 w-[130px]" data-testid={`dashboard-task-status-${task.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TASK_STATUSES.map((s) => (
            <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
